import { displayPortCode, searchPorts } from '@felix/domain';
import { Combobox, TextInput, useCombobox } from '@mantine/core';
import { IconAnchor } from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryName, usePortCatalog } from '../screens/ports/catalog-context.js';
import classes from './PortPicker.module.css';

export interface PortPickerValue {
  /** Gösterilen ad; listede yoksa yazılan serbest metin. */
  name: string;
  /** Liman listesindeki kod (UN/LOCODE ya da `WPI<numara>`); serbest metinde null. */
  code: string | null;
}

export interface PortPickerProps {
  label: ReactNode;
  value: PortPickerValue;
  onChange: (next: PortPickerValue) => void;
  withAsterisk?: boolean;
  error?: ReactNode;
  size?: 'xs' | 'sm' | 'md';
  placeholder?: string;
  disabled?: boolean;
  /** Yalnız listeden seçim (mesafe tablosu): serbest metin uyarısı hata gibi gösterilir. */
  strict?: boolean;
  classNames?: { label?: string };
  'aria-label'?: string;
}

/** Kutunun altındaki açıklama; `country` ülke adıdır (ekranın dilinde). */
export type PortPickerHint =
  | { kind: 'port'; text: string }
  | { kind: 'freeText' }
  | { kind: 'pickFromList' };

/** Ülke ve (varsa) UN/LOCODE; WPI iç kodu gösterilmez (ADR 0054 §1). */
export function portMeta(country: string, code: string): string {
  const shown = displayPortCode(code);
  return shown ? `${country} · ${shown}` : country;
}

/**
 * Açıklama (ADR 0054 §1): seçili limanda ülke ve kod; listede olmayan metinde uyarı yalnız değer
 * kabul edilince (alan odakta değilken) — yazarken çıkmaz.
 */
export function portPickerHint(input: {
  selected: { country: string; code: string } | null;
  freeText: boolean;
  strict: boolean;
  focused: boolean;
}): PortPickerHint | null {
  if (input.selected)
    return { kind: 'port', text: portMeta(input.selected.country, input.selected.code) };
  if (!input.freeText || input.focused) return null;
  return { kind: input.strict ? 'pickFromList' : 'freeText' };
}

/**
 * Ortak liman seçici (ADR 0048 §6): ad, diğer ad, kod ve ülkeye göre arama (aksan duyarsız),
 * büyük limanlar önce. Seçilmezse yazılan metin serbest metin olarak kalır (eski kayıtlar ve
 * listede olmayan limanlar). Liste paketlidir; internet gerekmez.
 */
export function PortPicker({
  label,
  value,
  onChange,
  withAsterisk,
  error,
  size = 'sm',
  placeholder,
  disabled,
  strict,
  classNames,
  'aria-label': ariaLabel,
}: PortPickerProps) {
  const { t } = useTranslation();
  const { catalog } = usePortCatalog();
  const countryName = useCountryName();
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });
  const [search, setSearch] = useState(value.name);
  const listId = useId();

  useEffect(() => setSearch(value.name), [value.name]);

  const options = useMemo(
    () =>
      catalog && search.trim() && !value.code
        ? searchPorts(catalog.ports, search, { limit: 12, countryName })
        : [],
    [catalog, search, value.code, countryName],
  );
  const [focused, setFocused] = useState(false);
  const selected = value.code ? catalog?.byCode.get(value.code) : undefined;
  const freeText = catalog !== null && value.name.trim() !== '' && value.code === null;
  const hintState = portPickerHint({
    selected: selected ? { country: countryName(selected.country), code: selected.code } : null,
    freeText,
    strict: !!strict,
    focused,
  });
  const hint =
    hintState === null
      ? null
      : hintState.kind === 'port'
        ? hintState.text
        : t(
            hintState.kind === 'pickFromList'
              ? 'ports.picker.pickFromList'
              : 'ports.picker.freeText',
          );
  const strictError = hintState?.kind === 'pickFromList';
  const chip = displayPortCode(value.code);

  return (
    <Combobox
      store={combobox}
      withinPortal
      onOptionSubmit={(code) => {
        const port = catalog?.byCode.get(code);
        if (!port) return;
        onChange({ name: port.name, code: port.code });
        setSearch(port.name);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          size={size}
          label={label}
          withAsterisk={withAsterisk ?? false}
          placeholder={placeholder ?? t('ports.picker.placeholder')}
          disabled={disabled ?? false}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          value={search}
          error={error ?? (strictError ? hint : null)}
          // Değer varken açıklama satırı hep yer kaplar: uyarı alandan çıkınca belirdiğinde altındaki
          // düğmeler kaymaz (tıklama boşa gitmez)
          description={strictError ? null : (hint ?? (value.name.trim() !== '' ? ' ' : null))}
          // Açıklama ve hata kutunun altında: yan yana alanlar hizalı kalır (ADR 0054 §1)
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          classNames={{
            label: classNames?.label ?? '',
            description: classes.hint ?? '',
          }}
          leftSection={
            <IconAnchor size={15} aria-hidden className={value.code ? classes.linked : undefined} />
          }
          rightSection={chip ? <span className={classes.code}>{chip}</span> : null}
          rightSectionWidth={chip ? 92 : undefined}
          rightSectionPointerEvents="none"
          aria-autocomplete="list"
          aria-controls={listId}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setSearch(next);
            onChange({ name: next, code: null });
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => {
            setFocused(true);
            combobox.openDropdown();
          }}
          onBlur={() => {
            setFocused(false);
            combobox.closeDropdown();
          }}
        />
      </Combobox.Target>
      <Combobox.Dropdown hidden={options.length === 0}>
        <Combobox.Options id={listId} mah={320} style={{ overflowY: 'auto' }}>
          {options.map((port) => (
            <Combobox.Option value={port.code} key={port.code} className={classes.option}>
              <span className={classes.optionName}>{port.name}</span>
              <span className={classes.optionMeta}>
                {portMeta(countryName(port.country), port.code)}
                {port.source === 'company' ? ` · ${t('ports.picker.company')}` : ''}
              </span>
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
